import React, { useState, useEffect, useCallback } from "react";
import {
  FileText, History, Info, CheckCircle2, ArrowRight, XCircle, Eye,
  ClipboardCheck, MailCheck, RotateCcw, ExternalLink, Phone, AtSign, Star,
} from "lucide-react";
import { Modal } from "./Modal";
import { Badge, DisciplineBadge } from "./Badges";
import { Loader } from "./PageHead";
import { EquipmentModal } from "./EquipmentModal";
import { fmtDate, money } from "../lib/format";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const STATUS_META = {
  submitted: { label: "Submitted", cls: "b-amber" },
  reviewed:  { label: "Reviewed",  cls: "b-blue" },
  quoted:    { label: "Quoted",    cls: "b-violet" },
  converted: { label: "Converted", cls: "b-green" },
  cancelled: { label: "Cancelled", cls: "b-slate" },
  draft:     { label: "Draft",     cls: "b-slate" },
};

const STEPS = [
  { key: "submitted", label: "Submitted",  icon: FileText },
  { key: "reviewed",  label: "Reviewed",   icon: ClipboardCheck },
  { key: "quoted",    label: "Quoted",     icon: MailCheck },
  { key: "converted", label: "Converted",  icon: ArrowRight },
];

function Stepper({ status }) {
  if (status === "cancelled") {
    return (
      <div className="rfq-stepper cancelled" data-testid="rfq-stepper">
        <span className="badge b-red"><span className="bd" /> Cancelled</span>
      </div>
    );
  }
  const idx = STEPS.findIndex((s) => s.key === status);
  return (
    <div className="rfq-stepper" data-testid="rfq-stepper">
      {STEPS.map((s, i) => {
        const done = i <= idx;
        const active = i === idx;
        const Icon = s.icon;
        return (
          <React.Fragment key={s.key}>
            <div className={`rfq-step ${done ? "done" : ""} ${active ? "active" : ""}`}>
              <span className="rfq-step-dot"><Icon size={13} /></span>
              <span className="rfq-step-lbl">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <span className={`rfq-step-bar ${i < idx ? "done" : ""}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function RfqDetailModal({ id, canEdit, onClose, onChanged }) {
  const [rfq, setRfq] = useState(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [prices, setPrices] = useState({}); // idx -> string
  const [historyOpen, setHistoryOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/rfqs/${id}`);
      setRfq(r.data);
      setInternalNotes(r.data.internal_notes || "");
      setQuoteNumber(r.data.quote_number || "");
      const p = {};
      (r.data.items || []).forEach((it, i) => {
        if (it.estimated_price != null) p[i] = String(it.estimated_price);
      });
      setPrices(p);
    } catch (e) { toast.error(apiErr(e)); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const buildItemsPatch = () => {
    const patch = [];
    (rfq?.items || []).forEach((it, i) => {
      const cur = it.estimated_price == null ? "" : String(it.estimated_price);
      const nxt = (prices[i] ?? "").trim();
      if (nxt !== cur) {
        const v = nxt === "" ? null : Number(nxt);
        if (nxt !== "" && !Number.isFinite(v)) return;
        patch.push({ index: i, estimated_price: v });
      }
    });
    return patch;
  };

  const saveDetails = async () => {
    if (!rfq) return;
    setBusy(true);
    try {
      const body = {};
      if ((internalNotes || "") !== (rfq.internal_notes || "")) body.internal_notes = internalNotes;
      if ((quoteNumber || "") !== (rfq.quote_number || "")) body.quote_number = quoteNumber;
      const itemsPatch = buildItemsPatch();
      if (itemsPatch.length) body.items = itemsPatch;
      if (Object.keys(body).length === 0) { toast.message("Nothing to save"); setBusy(false); return; }
      await api.patch(`/rfqs/${id}`, body);
      toast.success("RFQ updated");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const transition = async (toStatus) => {
    setBusy(true);
    try {
      const body = { status: toStatus };
      // Auto-save unsaved edits at the same time
      if ((internalNotes || "") !== (rfq.internal_notes || "")) body.internal_notes = internalNotes;
      if ((quoteNumber || "") !== (rfq.quote_number || "")) body.quote_number = quoteNumber;
      const itemsPatch = buildItemsPatch();
      if (itemsPatch.length) body.items = itemsPatch;
      await api.patch(`/rfqs/${id}`, body);
      toast.success(`Marked as ${STATUS_META[toStatus]?.label || toStatus}`);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const convert = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/rfqs/${id}/convert`);
      toast.success("Converted to job — no re-capture needed");
      onChanged && onChanged();
      onClose && onClose();
      // soft navigate handled by parent if needed
      return r.data;
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!rfq) return <Modal open title="Loading…" onClose={onClose}><Loader /></Modal>;

  const status = rfq.status || "submitted";
  const meta = STATUS_META[status] || STATUS_META.submitted;
  const isClosed = status === "converted" || status === "cancelled";

  const recomputedTotal = (rfq.items || []).reduce((acc, it, i) => {
    const raw = prices[i] != null ? prices[i] : it.estimated_price;
    const p = raw === "" || raw == null ? null : Number(raw);
    if (p == null || !Number.isFinite(p)) return acc;
    return acc + p * (Number(it.quantity) || 1);
  }, 0);

  const footer = (
    <>
      <button className="btn ghost" onClick={onClose} data-testid="rfq-detail-close">Close</button>
      {canEdit && !isClosed && (
        <button className="btn ghost" onClick={saveDetails} disabled={busy} data-testid="rfq-detail-save">
          {busy ? "Saving…" : "Save changes"}
        </button>
      )}
      {canEdit && status === "submitted" && (
        <button className="btn ghost" onClick={() => transition("reviewed")} disabled={busy} data-testid="rfq-mark-reviewed">
          <ClipboardCheck size={14} /> Mark Reviewed
        </button>
      )}
      {canEdit && (status === "submitted" || status === "reviewed") && (
        <button className="btn ghost" onClick={() => transition("quoted")} disabled={busy} data-testid="rfq-mark-quoted">
          <MailCheck size={14} /> Mark Quoted
        </button>
      )}
      {canEdit && !isClosed && (
        <button className="btn ghost" onClick={() => transition("cancelled")} disabled={busy}
          style={{ color: "var(--red)", borderColor: "#f3d6d4" }} data-testid="rfq-cancel">
          <XCircle size={14} /> Cancel
        </button>
      )}
      {canEdit && status === "cancelled" && (
        <button className="btn ghost" onClick={() => transition("submitted")} disabled={busy} data-testid="rfq-reopen">
          <RotateCcw size={14} /> Re-open
        </button>
      )}
      {canEdit && !isClosed && (
        <button className="btn gold" onClick={convert} disabled={busy} data-testid="rfq-convert-detail">
          <ArrowRight size={14} /> Convert to job
        </button>
      )}
    </>
  );

  return (
    <Modal open onClose={onClose} icon={FileText} wide
      title={rfq.reference || "Quote request"}
      subtitle={`${rfq.customer_name || "—"} · ${rfq.item_count || (rfq.items || []).length} item${(rfq.item_count || (rfq.items || []).length) !== 1 ? "s" : ""}`}
      footer={footer}>

      {/* Status + stepper */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <Badge label={meta.label} cls={meta.cls} testid="rfq-detail-status" />
        <span className="t-mut" style={{ fontSize: 12 }}>
          Submitted {fmtDate(rfq.submitted_at)} · ref <span className="mono t-strong">{rfq.reference}</span>
        </span>
      </div>
      <Stepper status={status} />

      {/* Submitter + customer panels */}
      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14, marginBottom: 14 }}>
        <div className="card pad" style={{ background: "var(--bg-soft, #fafafa)" }}>
          <div className="sec-h" style={{ marginBottom: 8 }}>
            <h3 style={{ fontSize: 13 }}>Submitted by</h3>
            {rfq.submitted_by_is_primary && <Badge label="Primary" cls="b-gold" />}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
            <div className="t-strong" data-testid="rfq-submitter-name">
              {rfq.submitted_by_name || "—"}
              {rfq.submitted_by_is_primary && <Star size={12} style={{ marginLeft: 6, color: "var(--gold)" }} fill="currentColor" />}
            </div>
            {rfq.submitted_by_email && (
              <div className="mono t-mut" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <AtSign size={12} /> {rfq.submitted_by_email}
              </div>
            )}
            {rfq.submitted_by_phone && (
              <div className="mono t-mut" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Phone size={12} /> {rfq.submitted_by_phone}
              </div>
            )}
          </div>
        </div>

        <div className="card pad" style={{ background: "var(--bg-soft, #fafafa)" }}>
          <div className="sec-h" style={{ marginBottom: 8 }}><h3 style={{ fontSize: 13 }}>Customer</h3></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
            <div className="t-strong" data-testid="rfq-customer-name">{rfq.customer_name || "—"}</div>
            {rfq.customer?.contact_person && <div className="t-mut">Contact: {rfq.customer.contact_person}</div>}
            {rfq.customer?.email && <div className="mono t-mut">{rfq.customer.email}</div>}
            {(rfq.customer?.city || rfq.customer?.province) && (
              <div className="t-mut">{[rfq.customer.city, rfq.customer.province].filter(Boolean).join(", ")}</div>
            )}
          </div>
        </div>
      </div>

      {/* Items table */}
      <div className="sec-h" style={{ marginTop: 8 }}>
        <h3 style={{ fontSize: 14 }}>Equipment ({(rfq.items || []).length})</h3>
        <span className="tag-soft mono">{money(recomputedTotal || rfq.estimated_total)}</span>
      </div>
      <div className="tbl-wrap" style={{ marginBottom: 14 }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 28 }}>#</th>
              <th>Description</th>
              <th>Serial / Part / Issue</th>
              <th>Discipline</th>
              <th style={{ textAlign: "right" }}>Qty</th>
              <th style={{ textAlign: "right" }}>Est. price (excl)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(rfq.items || []).length === 0 && <tr><td colSpan={7} className="t-mut">No items on this request.</td></tr>}
            {(rfq.items || []).map((it, i) => {
              const linked = !!it.equipment_id;
              return (
                <tr key={it.id || i} data-testid={`rfq-item-${i}`}>
                  <td className="mono t-mut">{i + 1}</td>
                  <td>
                    <div className="t-strong">{it.description || "—"}</div>
                    {it.manufacturer && <div className="t-mut" style={{ fontSize: 12 }}>{it.manufacturer}</div>}
                    {linked && (
                      <div className="t-mut" style={{ fontSize: 11.5, marginTop: 2 }}>
                        Last cal {fmtDate(it.equipment_last_calibration_date)} · {it.equipment_history_count || 0} event(s)
                      </div>
                    )}
                    {it.notes && <div className="t-mut" style={{ fontSize: 12, marginTop: 4, fontStyle: "italic" }}>“{it.notes}”</div>}
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {[it.serial_number && `SN ${it.serial_number}`, it.part_number && `PN ${it.part_number}`, it.issue_number && `IN ${it.issue_number}`].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td><DisciplineBadge name={it.discipline} /></td>
                  <td className="mono" style={{ textAlign: "right" }}>{it.quantity || 1}</td>
                  <td style={{ textAlign: "right" }}>
                    {canEdit && !isClosed ? (
                      <input className="input mono" style={{ padding: "4px 8px", textAlign: "right", width: 110, fontSize: 12 }}
                        type="number" step="0.01" min="0"
                        value={prices[i] ?? ""} placeholder="—"
                        onChange={(e) => setPrices((p) => ({ ...p, [i]: e.target.value }))}
                        data-testid={`rfq-item-price-${i}`} />
                    ) : (
                      <span className="mono">{money(it.estimated_price)}</span>
                    )}
                  </td>
                  <td>
                    {linked && (
                      <button className="icon-btn" title="View calibration history" onClick={() => setHistoryOpen(it.equipment_id)} data-testid={`rfq-item-history-${i}`}>
                        <Eye size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Customer notes (read-only) */}
      {rfq.customer_notes && (
        <div className="notice" style={{ marginBottom: 14 }}>
          <Info size={16} />
          <span><b>From the customer:</b> {rfq.customer_notes}</span>
        </div>
      )}

      {/* Internal fields */}
      {canEdit ? (
        <div className="form-grid" style={{ marginBottom: 4 }}>
          <div className="fld" style={{ gridColumn: "span 1" }}>
            <label>Sage quote / PO number</label>
            <input className="input" value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)}
              placeholder="e.g. Q-2026-0123" disabled={isClosed} data-testid="rfq-quote-number" />
          </div>
          <div className="fld" style={{ gridColumn: "span 2" }}>
            <label>Internal notes (not visible to customer)</label>
            <textarea className="input" rows={3} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Anything reception / lab needs to know"
              disabled={isClosed} data-testid="rfq-internal-notes" />
          </div>
        </div>
      ) : (rfq.internal_notes && (
        <div className="fld">
          <label>Internal notes</label>
          <div className="t-mut" style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{rfq.internal_notes}</div>
        </div>
      ))}

      {/* Converted job link */}
      {rfq.converted_job_id && (
        <div className="notice" style={{ marginTop: 10 }} data-testid="rfq-converted-link">
          <CheckCircle2 size={16} />
          <span>Converted to job — see Active Lab. <a href="/app/active-lab" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>Open <ExternalLink size={12} /></a></span>
        </div>
      )}

      {historyOpen && <EquipmentModal id={historyOpen} canEdit={false} onClose={() => setHistoryOpen(null)} onChanged={() => {}} />}
    </Modal>
  );
}
